import React, { useState, useRef, useEffect } from "react";
import "../styles/VideoContainer.css";
import {
  MdPause,
  MdPlayArrow,
  MdFullscreen,
  MdVolumeUp,
  MdVolumeOff,
  MdMoreVert,
  MdContentCopy,
} from "react-icons/md";

export default function VideoContainer() {
  const videoUrl =
    "https://d2qvtyho2ddefy.cloudfront.net/Nvision_AI_DEMO_VIDEO_20250909.mp4";

  const [videoPlaying, setVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [bufferedPercent, setBufferedPercent] = useState(0);

  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const optionsMenuRef = useRef(null);

  // Auto-play video on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch((error) => {
        console.error("Autoplay prevented: ", error);
        setVideoPlaying(false);
      });
      setVideoPlaying(true);
    }
  }, []);

  // Close options menu if clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(event.target)
      ) {
        if (!event.target.closest(".player-options-button")) {
          setShowOptionsMenu(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [optionsMenuRef]);

  const handleMouseEnter = () => setIsControlsVisible(true);
  const handleMouseLeave = () => {
    setIsControlsVisible(false);
    setShowOptionsMenu(false);
  };

  const handleProgress = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    const videoDuration = videoRef.current.duration;
    if (videoDuration > 0) {
      const buffered = videoRef.current.buffered;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        setBufferedPercent((bufferedEnd / videoDuration) * 100);
      }
    }
  };

  const handleDurationChange = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setVideoPlaying(true);
    } else {
      videoRef.current.pause();
      setVideoPlaying(false);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !duration) return;
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const seekTime = ((e.clientX - rect.left) / rect.width) * duration;
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleSpeedChange = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
      setShowOptionsMenu(false);
    }
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !videoRef.current.muted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  return (
    <div
      className="player-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="player-wrapper" ref={wrapperRef}>
        <video
          ref={videoRef}
          src={videoUrl}
          className="player-video"
          onTimeUpdate={handleProgress}
          onLoadedMetadata={handleDurationChange}
          onEnded={() => setVideoPlaying(false)}
          onClick={togglePlayPause}
          autoPlay
          muted
          loop
        />

        <div
          className={`player-overlay-controls ${
            isControlsVisible || !videoPlaying ? "visible" : ""
          }`}
        >
          <div className="player-controls-wrapper">
            <div className="player-controls">
              {/* Left Side Controls */}
              <div className="player-controls-left">
                <button className="player-button" onClick={togglePlayPause}>
                  {videoPlaying ? (
                    <MdPause className="player-icon" />
                  ) : (
                    <MdPlayArrow className="player-icon" />
                  )}
                </button>
                <div className="player-time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              {/* Progress Bar in the Middle */}
              <div className="player-progress-bar" onClick={handleSeek}>
                <div
                  className="player-progress-buffered"
                  style={{ width: `${bufferedPercent}%` }}
                />
                <div
                  className="player-progress-filled"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
                <div
                  className="player-progress-thumb"
                  style={{ left: `${(currentTime / duration) * 100}%` }}
                />
              </div>

              {/* Right Side Controls */}
              <div className="player-controls-right">
                <button className="player-button" onClick={toggleMute}>
                  {isMuted ? (
                    <MdVolumeOff className="player-icon" />
                  ) : (
                    <MdVolumeUp className="player-icon" />
                  )}
                </button>

                <div className="player-options-control" ref={optionsMenuRef}>
                  {showOptionsMenu && (
                    <div className="player-options-menu">
                      <div className="player-options-heading">Speed</div>
                      <div className="player-options-values">
                        {[0.5, 1, 1.5, 2].map((speed) => (
                          <button
                            key={speed}
                            onClick={() => handleSpeedChange(speed)}
                            className={playbackSpeed === speed ? "active" : ""}
                          >
                            {`${speed}x`}
                          </button>
                        ))}
                      </div>
                      <div className="player-options-divider" />
                      <div className="player-options-row">
                        <span>Quality</span>
                        <span className="player-options-row-value">Auto</span>
                      </div>
                      <div
                        className="player-options-row"
                        onClick={() => navigator.clipboard.writeText(videoUrl)}
                      >
                        <span>Copy link</span>
                        <MdContentCopy className="player-options-row-icon" />
                      </div>
                    </div>
                  )}
                  <button
                    className="player-button player-options-button"
                    onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                  >
                    <MdMoreVert className="player-icon" />
                  </button>
                </div>

                <button className="player-button" onClick={handleFullscreen}>
                  <MdFullscreen className="player-icon" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
